/**
 * UFC-1 — Unified Field Conflict Resolution: group `EvidenceConflict`s
 * by `(sectionId, fieldKey)` instead of the underlying
 * `(sectionId, field, docId)` triplet.
 *
 * Why: live-prod walkthrough 2026-05-18 (Sickel "Unresolved
 * Conflicts" panel) showed 5 separate rows for the SAME `parcel.acres`
 * field — one per supporting document. The user is forced to open 5
 * dropdowns and pick "provider" 5 times to clear one decision. The
 * data layer is correct (each `(provider, doc)` disagreement is a
 * legitimate data point) but the user-facing layer is wrong: the
 * question is "what's the right Acres value?" — singular.
 *
 * UFC is a pure UI-side grouping projection — the underlying
 * `EvidenceConflict[]` schema is untouched (regression invariant #1
 * from the pack). When a grouped resolution is applied, every
 * underlying `(sectionId, fieldKey, docId)` conflict in the group is
 * marked resolved atomically (regression invariant #2).
 *
 * Composes with:
 *   - ICR (shipped) — ICR's prefilter removes garbage doc values
 *     BEFORE they become conflicts; UFC groups what remains. Rows
 *     stamped `autoResolved: true` are filtered upstream by the panel
 *     and are not surfaced to the grouper.
 *   - PRV/EVL/UAE/RDX — UFC consumes the same `authoritative`,
 *     `authoredValue`, `reportValue` discriminators those packs
 *     already attach to the conflict row.
 *
 * Pure / no React dependency so it can be unit-tested in isolation.
 */
import type { EvidenceConflict } from '../services/evidenceConflict';
import type { SiteDocument } from '../types/siteRag';

/** UFC-3 — focus-param shape for the SED Variable Browser deep-link.
 *  We piggyback on the existing `?focus=variable:<id>` convention but
 *  widen `<id>` to allow a `<sectionId>.<fieldKey>` form. Old call
 *  sites that pass a bare sectionId keep working. */
export const FOCUS_DELIMITER = '.';

export interface FieldConflictGroupMember {
  /** Underlying conflict docId (or '' for authored / report-only rows). */
  docId: string;
  /** Display name resolved off the {@link SiteDocument} cache. Falls back
   *  to `c.docName` then `c.docId` when the doc has been evicted. */
  docName: string;
  /** The doc-extracted value (may be undefined for authored / report
   *  rows). */
  docValue: unknown;
  severity: EvidenceConflict['severity'];
  /** Carried through so the disclosure can render explanation tooltips
   *  without re-deriving them. */
  explanation: string;
  /** RDX-6 — report axis. Optional, only set for prior-report-derived
   *  conflicts (3-axis). */
  reportValue?: unknown;
  reportId?: string;
  /** ICR-4 — authored axis. */
  authoredValue?: unknown;
  authoredBy?: string;
}

export interface FieldConflictGroup {
  /** Composite key — stable across renders. `${sectionId}.${fieldKey}`. */
  key: string;
  sectionId: string;
  fieldKey: string;
  /** Pre-humanised label so the panel + the variable browser chip share
   *  one display string. */
  fieldLabel: string;
  /** Provider value carried by EVERY conflict in the group (the
   *  detector emits one provider value per `(sectionId, field)` slot;
   *  if the detector ever drifts and two underlying rows disagree on
   *  the provider value, we keep the first and surface the rest in
   *  the disclosure). */
  providerValue: unknown;
  /** Provider source string (e.g. `'FEMA NFHL'`) — used in the chip
   *  underneath the side-by-side preview. */
  providerSource: string;
  /** Underlying conflicts, one per disagreeing source. */
  members: FieldConflictGroupMember[];
  /** Worst-case severity across members — drives the chip colour on
   *  the cross-link badge. */
  worstSeverity: EvidenceConflict['severity'];
  /** `authoritative` carried up from the first member (every underlying
   *  row in a group SHOULD share this value — they were classified the
   *  same way). Used by the panel to gate the one-click Confirm
   *  affordance. */
  authoritative?: EvidenceConflict['authoritative'];
  /** True when at least one member carries an `authoredValue`. */
  hasAuthored: boolean;
  /** True when at least one member carries a `reportValue`. */
  hasReport: boolean;
}

const SEVERITY_RANK: Record<EvidenceConflict['severity'], number> = {
  low: 1,
  medium: 2,
  high: 3,
};

/**
 * UFC-1 — `camelCase` / `snake_case` → `"Camel Case"` / `"Snake Case"`
 * with the first letter capitalised. Shared between the Conflicts panel
 * (group header) and the SED Variable Browser conflict chip so the
 * two surfaces can't drift on the same field.
 *
 * Stand-alone export (not just a panel-internal helper) so tests can
 * pin the contract without rendering React.
 */
export function humanizeField(field: string): string {
  if (!field) return field;
  const spaced = field
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
  if (!spaced) return field;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * UFC-3 — composite focus token for a `(sectionId, fieldKey)` pair.
 * Centralised so the panel writer and the variable browser receiver
 * use the same shape (R4 — exact key, no fuzzy matching).
 */
export function buildFieldFocusId(sectionId: string, fieldKey: string): string {
  return `${sectionId}${FOCUS_DELIMITER}${fieldKey}`;
}

/**
 * UFC-3 — inverse of {@link buildFieldFocusId}. Returns `null` when the
 * string isn't a `<sectionId>.<fieldKey>` shape (legacy
 * `?focus=variable:<sectionId>` callers fall through to a bare
 * sectionId scroll, preserving back-compat).
 */
export function parseFieldFocusId(
  id: string,
): { sectionId: string; fieldKey: string } | null {
  if (!id) return null;
  const idx = id.indexOf(FOCUS_DELIMITER);
  if (idx <= 0 || idx === id.length - 1) return null;
  return {
    sectionId: id.slice(0, idx),
    fieldKey: id.slice(idx + 1),
  };
}

/**
 * UFC-1 — group an `EvidenceConflict[]` by `(sectionId, fieldKey)`.
 *
 * Stable ordering — groups come out in first-seen order, members in
 * first-seen order. The panel applies its own severity sort on top so
 * the grouper stays purely structural.
 *
 * @param conflicts the unresolved (post-ICR-filter) conflict list.
 * @param docs the live SiteDocument array — used to resolve
 *             `docId → fileName` for the disclosure rows. Optional;
 *             the grouper falls back to `c.docName` then `c.docId`
 *             when the doc lookup misses (legacy SEDs that referenced
 *             an evicted doc).
 */
export function groupConflictsByField(
  conflicts: readonly EvidenceConflict[],
  docs?: readonly SiteDocument[],
): FieldConflictGroup[] {
  const docsById = new Map<string, SiteDocument>();
  for (const d of docs ?? []) docsById.set(d.id, d);

  const groups = new Map<string, FieldConflictGroup>();

  for (const c of conflicts) {
    if (!c) continue;
    // R4 — exact key, no fuzzy matching. If the detector ever produces
    // 'parcel.acres' vs 'parcel.acres ' (trailing space), the two rows
    // would land in different groups — that's a detector bug to fix
    // upstream, not a grouper concern.
    const key = `${c.sectionId}${FOCUS_DELIMITER}${c.field}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        sectionId: c.sectionId,
        fieldKey: c.field,
        fieldLabel: humanizeField(c.field),
        providerValue: c.providerValue,
        providerSource: c.providerSource ?? '',
        members: [],
        worstSeverity: c.severity,
        authoritative: c.authoritative,
        hasAuthored: false,
        hasReport: false,
      };
      groups.set(key, group);
    }

    // Member projection — resolve doc name off the live doc list when
    // possible. The DocViewerDialog opener (per-doc disclosure
    // "Open doc" link) reads `docId` directly, so the resolver only
    // affects the displayed label.
    const docMeta = docsById.get(c.docId);
    const docName =
      docMeta?.fileName ??
      c.docName ??
      (c.docId || '');

    group.members.push({
      docId: c.docId,
      docName,
      docValue: c.docValue,
      severity: c.severity,
      explanation: c.explanation,
      reportValue: c.reportValue,
      reportId: c.reportId,
      authoredValue: c.authoredValue,
      authoredBy: c.authoredBy,
    });

    // Aggregate worst-severity per group so the cross-link chip on the
    // variable row can colour itself by the most-severe member.
    if (SEVERITY_RANK[c.severity] > SEVERITY_RANK[group.worstSeverity]) {
      group.worstSeverity = c.severity;
    }

    if (c.authoredValue !== undefined) group.hasAuthored = true;
    if (c.reportValue !== undefined) group.hasReport = true;
  }

  return Array.from(groups.values());
}

/**
 * UFC-3 — count unresolved conflicts indexed by the SED Variable
 * Browser's `path` (`${domain}.data.${key}`) so each variable row can
 * render a `⚠ N sources disagree · [Resolve →]` chip without
 * re-walking the entire conflict list on every render.
 *
 * Sums member counts per `(sectionId, field)` group — the chip
 * surfaces "5 sources disagree" for the parcel.acres example, not
 * "5 conflicts" (which would be technically correct at the data layer
 * but misleading at the question-the-user-is-answering layer).
 *
 * Auto-resolved + user-resolved rows are excluded — same predicate the
 * panel applies before grouping.
 */
export function indexConflictsByVariablePath(
  conflicts: readonly EvidenceConflict[] | null | undefined,
): Map<string, number> {
  const out = new Map<string, number>();
  if (!conflicts || conflicts.length === 0) return out;
  for (const c of conflicts) {
    if (!c) continue;
    if (c.userResolution) continue;
    if (c.autoResolved) continue;
    const path = `${c.sectionId}.data.${c.field}`;
    out.set(path, (out.get(path) ?? 0) + 1);
  }
  return out;
}
